import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useI18n } from '../../contexts/I18nContext';
import { useUiVisibility } from '../../contexts/UiVisibilityContext';
import { ProfileModal } from './ProfileModal';
import { StatisticsModal } from './StatisticsModal';
import { SystemValidationTests } from './SystemValidationTests';
import { LanguageSelector } from '../common/LanguageSelector';
import config from '../../config/env';
import { safeStorage } from '../../utils/safeStorage';
import './DebugMenu.css';

export const DebugMenu: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const { theme, setTheme } = useTheme();
  const { t } = useI18n();
  const { showDebugMenu, debugMenuUsernames, ready: visibilityReady } = useUiVisibility();
  const [isOpen, setIsOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [showValidationTests, setShowValidationTests] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Only show in development mode when authenticated
  const isDebugMode = config.dev.debug;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMenuClick = (action: 'profile' | 'stats' | 'validation' | 'export' | 'import' | 'reset') => {
    setIsOpen(false);

    if (action === 'profile') {
      setShowProfileModal(true);
    } else if (action === 'stats') {
      setShowStatsModal(true);
    } else if (action === 'validation') {
      setShowValidationTests(true);
    } else if (action === 'export') {
      handleExportData();
    } else if (action === 'import') {
      handleImportData();
    } else if (action === 'reset') {
      handleResetSettings();
    }
  };

  const handleThemeToggle = () => {
    const newMode = theme.mode === 'light' ? 'dark' : theme.mode === 'dark' ? 'auto' : 'light';
    setTheme({ ...theme, mode: newMode });
    setIsOpen(false);
  };

  const handleExportData = () => {
    try {
      const exportData = {
        theme: safeStorage.getItem('theme-config'),
        language: safeStorage.getItem('preferred-language'),
        apiStats: safeStorage.getItem('api_stats'),
        timestamp: new Date().toISOString(),
        version: '1.0.0'
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `provisioning-settings-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('[DEBUG MENU] Export failed:', error);
    }
  };

  const handleImportData = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = JSON.parse(e.target?.result as string);
            if (data.theme) safeStorage.setItem('theme-config', data.theme);
            if (data.language) safeStorage.setItem('preferred-language', data.language);
            if (data.apiStats) safeStorage.setItem('api_stats', data.apiStats);
            window.location.reload();
          } catch (error) {
            console.error('[DEBUG MENU] Import failed:', error);
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };

  const handleResetSettings = () => {
    if (window.confirm(t('debug.confirmReset'))) {
      safeStorage.removeItem('theme-config');
      safeStorage.removeItem('preferred-language');
      safeStorage.removeItem('api_stats');
      window.location.reload();
    }
  };

  if (!isDebugMode || !isAuthenticated) {
    return null;
  }
  // Wait for the server-side ui_settings to resolve before painting — avoids
  // a flash where the menu renders for everyone then disappears once the
  // allowlist arrives.
  if (!visibilityReady) {
    return null;
  }
  // Per-user allowlist takes priority. Non-empty list → only listed users see
  // the menu. Empty list → fall back to the global/role `show_debug_menu`
  // boolean (preserves existing behaviour). We match against
  // `preferredUsername` (the Keycloak `preferred_username` claim — stable
  // and unique per realm), NOT the display `name` which may collide.
  const username = user?.preferredUsername ?? null;
  if (debugMenuUsernames.length > 0) {
    if (!username || !debugMenuUsernames.includes(username)) {
      return null;
    }
  } else if (!showDebugMenu) {
    return null;
  }

  return (
    <>
      <div className="debug-menu" ref={dropdownRef}>
        <button
          className="debug-menu-trigger"
          onClick={() => setIsOpen(!isOpen)}
          title={t('debug.debugMenuTitle')}
        >
          <span className="debug-icon">🐛</span>
          <span className="debug-text">Debug</span>
          <span className={`debug-arrow ${isOpen ? 'open' : ''}`}>▼</span>
        </button>

        {isOpen && (
          <div className="debug-menu-dropdown">
            <div className="debug-menu-section">
              <div className="debug-menu-section-title">{t('debug.profileInformation')}</div>
              <button
                className="debug-menu-item"
                onClick={() => handleMenuClick('profile')}
              >
                <span className="debug-menu-icon">👤</span>
                <span className="debug-menu-label">{t('debug.profileInformation')}</span>
                <span className="debug-menu-desc">{t('debug.viewFullDetails')}</span>
              </button>
            </div>

            <div className="debug-menu-section">
              <div className="debug-menu-section-title">{t('debug.fullStatistics')}</div>
              <button
                className="debug-menu-item"
                onClick={() => handleMenuClick('stats')}
              >
                <span className="debug-menu-icon">📊</span>
                <span className="debug-menu-label">{t('debug.fullStatistics')}</span>
                <span className="debug-menu-desc">{t('debug.apiCallsAndStats')}</span>
              </button>
            </div>

            <div className="debug-menu-section">
              <div className="debug-menu-section-title">{t('debug.systemValidation')}</div>
              <button
                className="debug-menu-item"
                onClick={() => handleMenuClick('validation')}
              >
                <span className="debug-menu-icon">🧪</span>
                <span className="debug-menu-label">{t('debug.systemValidation')}</span>
                <span className="debug-menu-desc">{t('debug.validateApiApp')}</span>
              </button>
            </div>

            <div className="debug-menu-section">
              <div className="debug-menu-section-title">{t('debug.languageSettings')}</div>
              <div className="debug-menu-language-selector">
                <LanguageSelector variant="inline" compact={true} />
              </div>
            </div>

            <div className="debug-menu-section">
              <div className="debug-menu-section-title">{t('debug.themeSettings')}</div>
              <button
                className="debug-menu-item theme-toggle"
                onClick={handleThemeToggle}
              >
                <span className="debug-menu-icon">
                  {theme.mode === 'light' ? '☀️' : theme.mode === 'dark' ? '🌙' : '🔄'}
                </span>
                <span className="debug-menu-label">
                  {t('theme.themeMode')}: {t(`theme.${theme.mode}Mode`)}
                </span>
                <span className="debug-menu-desc">{t('theme.switchedTo')} {t(`theme.${theme.mode === 'light' ? 'dark' : theme.mode === 'dark' ? 'auto' : 'light'}Mode`)}</span>
              </button>
            </div>

            <div className="debug-menu-section">
              <div className="debug-menu-section-title">{t('debug.dataManagement')}</div>
              <button
                className="debug-menu-item"
                onClick={() => handleMenuClick('export')}
              >
                <span className="debug-menu-icon">📤</span>
                <span className="debug-menu-label">{t('debug.exportData')}</span>
                <span className="debug-menu-desc">{t('debug.exportSettings')}</span>
              </button>

              <button
                className="debug-menu-item"
                onClick={() => handleMenuClick('import')}
              >
                <span className="debug-menu-icon">📥</span>
                <span className="debug-menu-label">{t('debug.importData')}</span>
                <span className="debug-menu-desc">{t('debug.importSettings')}</span>
              </button>

              <button
                className="debug-menu-item danger"
                onClick={() => handleMenuClick('reset')}
              >
                <span className="debug-menu-icon">🔄</span>
                <span className="debug-menu-label">{t('debug.resetSettings')}</span>
                <span className="debug-menu-desc">{t('debug.resetAllSettings')}</span>
              </button>
            </div>
          </div>
        )}
      </div>

      <ProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        user={user}
      />

      <StatisticsModal
        isOpen={showStatsModal}
        onClose={() => setShowStatsModal(false)}
      />

      <SystemValidationTests
        isOpen={showValidationTests}
        onClose={() => setShowValidationTests(false)}
      />
    </>
  );
};